import { useCallback, useEffect, useMemo, useRef } from 'react';

import { AxiosError } from 'axios';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet } from 'react-router-dom';

import Container from '../components/layout/container';
import AdminHeader from '../components/layout/header/admin';
import Sidebar from '../components/layout/sidebar';
import MobileSidebar from '../components/layout/sidebar/mobile';
import ChatBot from '../pages/chat-bot';
import { setData, setInitialLoad, setParams, setTotal } from '../store/actions/requester';
import { type Requester } from '../store/reducers/requester';
import { requesterSelector } from '../store/selectors/requester';
import { ToastError } from '../utils/toastify';

const AdminLayout = () => {
  const dispatch = useDispatch();
  const bottomDivRef = useRef(null);
  const requester = useSelector(requesterSelector);

  const doRequest = useCallback(async () => {
    if (requester.request && requester.params) {
      try {
        const data = await requester.request(requester.params);
        const nextSkip = requester.params.skip + 20;
        dispatch(setData({ data: [...requester.data, ...data.list] }));
        dispatch(setTotal({ total: data.totalCount }));
        dispatch(setInitialLoad({ initialLoaded: true }));
        dispatch(setParams({ skip: nextSkip < data.totalCount ? nextSkip : data.totalCount }));
      } catch (error) {
        if (error instanceof AxiosError) {
          ToastError(
            typeof error.response?.data.message === 'string'
              ? error.response?.data.message
              : Object.values(error.response?.data.message).join(', '),
          );
        } else {
          ToastError('Something went wrong');
        }
      }
    }
  }, [requester, dispatch]);

  const doDebouncedCall = useMemo(
    () =>
      _.debounce(async (requesterData: Requester) => {
        if (requesterData.request && requesterData.params) {
          try {
            const data = await requesterData.request(requesterData.params);
            const nextSkip = requesterData.params.skip + 20;
            dispatch(setData({ data: [...requesterData.data, ...data.list] }));
            dispatch(setTotal({ total: data.totalCount }));
            dispatch(setInitialLoad({ initialLoaded: true }));
            dispatch(setParams({ skip: nextSkip < data.totalCount ? nextSkip : data.totalCount }));
          } catch (error) {
            if (error instanceof AxiosError) {
              ToastError(
                typeof error.response?.data.message === 'string'
                  ? error.response?.data.message
                  : Object.values(error.response?.data.message).join(', '),
              );
            } else {
              ToastError('Something went wrong');
            }
          }
        }
      }, 400),
    [],
  );

  useEffect(() => {
    (async () => {
      if (!requester.initialLoaded) {
        if (requester.debouncedCall) {
          await doDebouncedCall(requester);
        } else {
          await doRequest();
        }
      }
    })();
  }, [requester, doRequest, doDebouncedCall]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      async ([entry]) => {
        if (entry.isIntersecting && requester.request && requester.params.skip < requester.total) {
          if (requester.debouncedCall) {
            await doDebouncedCall(requester);
          } else {
            await doRequest();
          }
        }
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.1,
      },
    );

    if (bottomDivRef.current) {
      observer.observe(bottomDivRef.current);
    }

    return () => {
      if (bottomDivRef.current) {
        observer.unobserve(bottomDivRef.current);
      }
    };
  }, [requester, bottomDivRef, dispatch, doRequest, doDebouncedCall]);

  return (
    <Container>
      <AdminHeader />
      <div className="w-full h-full flex flex-row xl:items-start justify-start overflow-auto mobile:mt-[90px]">
        <Sidebar className=" mobile:hidden" />
        <div className="relative w-full overflow-auto p-[30px] mobile:!p-[15px] medium:p-[20px] bg-[#F9F9F9] mobile:h-[calc(100vh_-_180px)] h-[calc(100vh_-_90px)]">
          <Outlet />
          <div ref={bottomDivRef} className="h-[1px] w-full"></div>
          <ChatBot />
        </div>
      </div>
      <MobileSidebar className="hidden mobile:flex mobile:h-[90px]" />
    </Container>
  );
};

export default AdminLayout;
